import { useId, type ReactNode } from "react";
import { Bookmark, CalendarDays, House, Image, LayoutGrid, PencilRuler, Radio, TrendingUp } from "lucide-react";

/*
 * The nav glyphs (CreativeOS Library.lib.pen, Foundations / Icons and logos,
 * Navigation glyphs; Nav item, style B, Emad 1 Oct option 2). Lucide at 18px,
 * except Universe's orbital mark and Strategy's bullseye, drawn at lucide
 * weight. The glyph is its outline until the item is selected, then it is the
 * same glyph filled (Filled below): the ink turns to the accent through .co-nav
 * in cockpit.css. Paths are the library's own, on a 24 grid drawn at 18.
 */
export type NavGlyphName =
  | "home"
  | "universe"
  | "strategy"
  | "signals"
  | "trends"
  | "references"
  | "workshop"
  | "calendar"
  | "agent-runs"
  | "media";

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "none" }}>
      {children}
    </svg>
  );
}

const LUCIDE = { size: 18, strokeWidth: 2, "aria-hidden": true, style: { flex: "none" } } as const;

function Outline({ name }: { name: NavGlyphName }) {
  switch (name) {
    case "home":
      return <House {...LUCIDE} />;
    case "universe":
      return (
        <Svg>
          <path strokeWidth={2} d="M8.8 12a3.2 3.2 0 1 0 6.4 0 3.2 3.2 0 1 0-6.4 0m13.2 0a10 4.4 0 1 0-20 0 10 4.4 0 1 0 20 0m-5 8.66a10 4.4 60 1 0-10-17.32 10 4.4 60 1 0 10 17.32" />
        </Svg>
      );
    case "strategy":
      return (
        <Svg>
          <path strokeWidth={2} d="M3.5 12a8.5 8.5 0 1 0 17 0 8.5 8.5 0 1 0-17 0m4.5 0a4 4 0 1 0 8 0 4 4 0 1 0-8 0" />
          <path fill="currentColor" stroke="none" d="M10.9 12a1.1 1.1 0 1 0 2.2 0 1.1 1.1 0 1 0-2.2 0" />
        </Svg>
      );
    case "signals":
      return <Radio {...LUCIDE} />;
    case "trends":
      return <TrendingUp {...LUCIDE} />;
    case "references":
      return <Bookmark {...LUCIDE} />;
    case "workshop":
      return <PencilRuler {...LUCIDE} />;
    case "calendar":
      return <CalendarDays {...LUCIDE} />;
    case "agent-runs":
      return <LayoutGrid {...LUCIDE} />;
    case "media":
      return <Image {...LUCIDE} />;
  }
}

/**
 * The selected glyph: the shape filled in the item's ink, with its details cut
 * out of the fill (a mask, so they show whatever is behind) or kept as strokes
 * where they stand outside the shape. Same 24 grid and stroke weight.
 */
function Filled({ name }: { name: NavGlyphName }) {
  const id = useId();
  const mask = (cuts: ReactNode) => (
    <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
      <rect width="24" height="24" fill="#fff" />
      <g stroke="#000" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {cuts}
      </g>
    </mask>
  );
  const ink = { fill: "currentColor", stroke: "currentColor", strokeWidth: 2 } as const;
  switch (name) {
    case "home":
      return (
        <Svg>
          {mask(<path fill="#000" stroke="none" d="M9 21v-8a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v8z" />)}
          <path {...ink} mask={`url(#${id})`} d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </Svg>
      );
    case "universe":
      return (
        <Svg>
          <path strokeWidth={2} d="m22 12a10 4.4 0 1 0-20 0 10 4.4 0 1 0 20 0m-5 8.66a10 4.4 60 1 0-10-17.32 10 4.4 60 1 0 10 17.32" />
          <circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="currentColor" strokeWidth={2} />
        </Svg>
      );
    case "strategy":
      return (
        <Svg>
          {mask(<circle cx="12" cy="12" r="4" />)}
          <circle {...ink} mask={`url(#${id})`} cx="12" cy="12" r="8.5" />
        </Svg>
      );
    case "signals":
      return (
        <Svg>
          <path strokeWidth={2} d="M16.247 7.761a6 6 0 0 1 0 8.478M19.075 4.933a10 10 0 0 1 0 14.134M4.925 19.067a10 10 0 0 1 0-14.134M7.753 16.239a6 6 0 0 1 0-8.478" />
          <circle cx="12" cy="12" r="2" fill="currentColor" stroke="currentColor" strokeWidth={2} />
        </Svg>
      );
    case "trends":
      return (
        <Svg>
          <path strokeWidth={3} d="M16 7h6v6M22 7l-8.5 8.5-5-5L2 17" />
        </Svg>
      );
    case "references":
      return (
        <Svg>
          <path {...ink} d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
        </Svg>
      );
    case "workshop":
      return (
        <Svg>
          <path strokeWidth={2} d="M13 7 8.7 2.7a2.41 2.41 0 0 0-3.4 0L2.7 5.3a2.41 2.41 0 0 0 0 3.4L7 13M17 11l4.3 4.3c.94.94.94 2.46 0 3.4l-2.6 2.6c-.94.94-2.46.94-3.4 0L11 17M8 6l2-2M18 16l2-2" />
          {mask(<path d="m15 5 4 4" />)}
          <path {...ink} mask={`url(#${id})`} d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
        </Svg>
      );
    case "calendar":
      return (
        <Svg>
          <path strokeWidth={2} d="M8 2v4M16 2v4" />
          {mask(<path d="M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />)}
          <rect {...ink} mask={`url(#${id})`} width="18" height="18" x="3" y="4" rx="2" />
        </Svg>
      );
    case "agent-runs":
      return (
        <Svg>
          <rect {...ink} width="7" height="7" x="3" y="3" rx="1" />
          <rect {...ink} width="7" height="7" x="14" y="3" rx="1" />
          <rect {...ink} width="7" height="7" x="14" y="14" rx="1" />
          <rect {...ink} width="7" height="7" x="3" y="14" rx="1" />
        </Svg>
      );
    case "media":
      return (
        <Svg>
          {mask(
            <>
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            </>,
          )}
          <rect {...ink} mask={`url(#${id})`} width="18" height="18" x="3" y="3" rx="2" ry="2" />
        </Svg>
      );
  }
}

/** One nav destination's mark: the outline, or the filled glyph when its item is selected. */
export function NavGlyph({ name, active = false }: { name: NavGlyphName; active?: boolean }) {
  return active ? <Filled name={name} /> : <Outline name={name} />;
}
